import { AppLauncher, TenantSelector } from "@virtus-automation/platform-ui";
import {
  BarChart3,
  Bell,
  Bot,
  Calendar,
  ChevronRight,
  FileText,
  Home,
  Info,
  Menu,
  Plus,
  RefreshCw,
  Search,
  TrendingUp,
  Users,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Tooltip } from "../../components";
import UserProfileMenu from "../../components/user/UserProfileMenu";
import { useActiveContext } from "../../contexts/ActiveContextContext";
import { useTranslation } from "../../contexts/LanguageContext";
import {
  PageSubHeaderProvider,
  usePageSubHeader,
} from "../../contexts/PageSubHeaderContext";
import { formatPageTitle } from "../../utils/seo";
import {
  getPageIcon as getGlobalPageIcon,
  getPageParent,
  getPageTitle as getGlobalPageTitle,
} from "../config/navigation";
import Sidebar from "./Sidebar";

// ─── Inner (lê context e renderiza a strip) ─────────────────────────────────
const DashboardLayoutInner: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { subtitle, actions } = usePageSubHeader();
  const { language, setLanguage, t } = useTranslation();
  const { isRetrying, error: contextError } = useActiveContext();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  // Responsividade
  const [isMobile, setIsMobile] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem("sidebar-collapsed") === "true";
    } catch {
      return false;
    }
  });
  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  // Search
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Notifications (placeholder)
  const notifCount = 0;

  // Estável para o React.memo da Sidebar
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("sidebar-collapsed", String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  // Detectar tamanho da tela
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 1024) setSidebarOpen(false);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Atalho '/' → foca pesquisa (padrão GitHub/YouTube/Linear)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "/") {
        const target = e.target as HTMLElement;
        const tag = target.tagName.toLowerCase();
        if (
          tag !== "input" &&
          tag !== "textarea" &&
          !target.isContentEditable
        ) {
          e.preventDefault();
          searchInputRef.current?.focus();
        }
      }
      // Ctrl+B recolhe a sidebar (mesmo atalho em todos os apps Virtus). Em campos de texto
      // fica com o editor (negrito).
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "b") {
        const target = e.target as HTMLElement;
        const tag = target.tagName.toLowerCase();
        if (tag === "input" || tag === "textarea" || target.isContentEditable)
          return;
        e.preventDefault();
        if (!isMobile) toggleCollapsed();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isMobile, toggleCollapsed]);

  const getPageTitle = useCallback(
    () => getGlobalPageTitle(location.pathname),
    [location.pathname],
  );

  const pageParent = getPageParent(location.pathname);

  // Com grupo no título da aba: "Insights · Posts (Orgânico)" (há 3 "Insights")
  useEffect(() => {
    const title = t(getPageTitle());
    document.title = formatPageTitle(
      pageParent ? `${title} · ${t(pageParent)}` : title,
    );
  }, [getPageTitle, pageParent, t]);

  const getPageIcon = () => getGlobalPageIcon(location.pathname);

  return (
    <div className="flex flex-row h-screen w-full max-w-full m-0 p-0 bg-white light-skin overflow-hidden">
      <Sidebar
        collapsed={isCollapsed}
        onToggleCollapsed={toggleCollapsed}
        isMobile={isMobile}
        mobileOpen={sidebarOpen}
        onCloseMobile={closeSidebar}
      />

      {/* ── Main content ──────────────────────────────────────────── */}
      <div
        className="flex-1 flex flex-col overflow-hidden min-w-0"
        onTouchStart={(e) => setTouchStartX(e.touches[0].clientX)}
        onTouchEnd={(e) => {
          if (touchStartX === null) return;
          const dx = e.changedTouches[0].clientX - touchStartX;
          if (dx > 60 && !sidebarOpen) setSidebarOpen(true);
          if (dx < -60 && sidebarOpen) setSidebarOpen(false);
          setTouchStartX(null);
        }}
      >
        {/* ── Mobile Header: ☰ | Logo | Avatar ─────────────────── */}
        <header className="lg:hidden w-full flex items-center h-12 px-3 bg-white border-b border-purple-200 shadow-xs shrink-0 z-30 gap-2">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-1.5 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-md transition-colors shrink-0"
            aria-label="Abrir menu"
          >
            <Menu className="h-4 w-4" />
          </button>
          <Link
            to="/"
            className="flex items-center flex-1 min-w-0 justify-center active:scale-95 transition-transform"
          >
            <div className="flex items-center justify-center gap-1.5">
              <img
                src="/logo.png"
                alt="Virtus Logo"
                className="h-5 w-auto shrink-0 object-contain"
              />
              <span className="rounded-md bg-purple-100 px-1.5 py-0.5 text-[11px] font-bold tracking-wider text-purple-700">
                SMM
              </span>
            </div>
          </Link>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setLanguage(language === "pt" ? "en" : "pt")}
              className="flex items-center justify-center p-1.5 rounded-lg border border-purple-200 bg-gray-50 text-xs font-bold text-purple-700"
              title={
                language === "pt" ? "Switch to English" : "Mudar para Português"
              }
            >
              {language === "pt" ? "PT" : "EN"}
            </button>
            <AppLauncher currentApp="smm" portalUrl={window.location.origin} />
            <UserProfileMenu variant="icon-only" />
          </div>
        </header>

        {/* ── Desktop Header ─────────────────────────────────── */}
        <header className="hidden lg:flex w-full items-center h-14 px-4 xl:px-5 bg-white border-b border-purple-200 shadow-xs shrink-0 z-30 gap-3">
          {/* Left: Breadcrumb / título da página */}
          {(() => {
            const PageIcon = getPageIcon();
            const title = getPageTitle();
            return (
              <div
                className="flex items-center gap-1.5 min-w-0 shrink-0"
                style={{ minWidth: 160 }}
              >
                <PageIcon
                  className={`shrink-0 ${pageParent ? "h-4 w-4 text-purple-400" : "h-5 w-5 text-purple-600"}`}
                />
                {pageParent && (
                  <>
                    <span className="text-sm text-gray-400 font-medium whitespace-nowrap">
                      {t(pageParent)}
                    </span>
                    <ChevronRight className="h-3.5 w-3.5 text-gray-300 shrink-0" />
                  </>
                )}
                <h1
                  className={`font-semibold text-gray-800 truncate ${pageParent ? "text-sm" : "text-base"}`}
                >
                  {t(title)}
                </h1>
              </div>
            );
          })()}

          {/* Center: Barra de pesquisa global */}
          <div className="flex-1 max-w-xl mx-auto relative">
            <div
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200 ${
                searchFocused
                  ? "border-purple-400 bg-white shadow-xs ring-2 ring-purple-100"
                  : "border-purple-200 bg-gray-50 hover:border-purple-300 hover:bg-white"
              }`}
            >
              <Search className="h-4 w-4 text-gray-400 shrink-0" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder={t("Pesquisar posts, campanhas, conteúdos...")}
                className="flex-1 bg-transparent text-sm text-gray-700 placeholder-gray-400 focus:outline-hidden"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
              />
              <div className="flex items-center gap-0.5 shrink-0">
                <kbd className="px-1.5 py-0.5 text-[10px] font-mono bg-gray-100 border border-purple-200 rounded-sm text-gray-400">
                  /
                </kbd>
              </div>
            </div>
            {/* Search suggestions dropdown */}
            {searchFocused && (
              <div className="absolute top-full left-0 right-0 mt-1.5 bg-white rounded-lg shadow-xl border border-purple-200 py-1.5 z-50">
                <p className="px-3 pt-0.5 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                  {searchQuery ? t("Resultados") : t("Acesso rápido")}
                </p>
                {[
                  { name: "Dashboard", href: "/", icon: Home },
                  { name: "Postagens", href: "/posts", icon: FileText },
                  { name: "Novo Anúncio", href: "/ads/new", icon: Plus },
                  { name: "Anúncios", href: "/ads", icon: TrendingUp },
                  {
                    name: "Cronograma IA",
                    href: "/posts/ai-schedule",
                    icon: Bot,
                  },
                  {
                    name: "Calendário",
                    href: "/posts/calendar",
                    icon: Calendar,
                  },
                  { name: "Leads", href: "/leads", icon: Users },
                  {
                    name: "Analytics & IA",
                    href: "/analytics",
                    icon: BarChart3,
                  },
                ]
                  .filter(
                    (i) =>
                      !searchQuery ||
                      i.name.toLowerCase().includes(searchQuery.toLowerCase()),
                  )
                  .map(({ name, href, icon: Ico }) => (
                    <Link
                      key={href}
                      to={href}
                      onClick={() => {
                        setSearchFocused(false);
                        setSearchQuery("");
                      }}
                      className="flex items-center gap-2.5 px-3 py-1.5 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors"
                    >
                      <Ico className="h-4 w-4 text-gray-400 shrink-0" />
                      <span>{t(name)}</span>
                    </Link>
                  ))}
                {searchQuery &&
                  ![
                    "Dashboard",
                    "Gerenciar Posts",
                    "Cronograma IA",
                    "Calendário",
                    "Analytics & IA",
                    "Mapa de Empatia",
                  ].some((n) =>
                    n.toLowerCase().includes(searchQuery.toLowerCase()),
                  ) && (
                    <p className="px-3 py-2 text-sm text-gray-400 italic">
                      {t("Nenhum resultado encontrado")}
                    </p>
                  )}
              </div>
            )}
          </div>

          {/* Right: Notificações + Contexto + Perfil */}
          <div className="flex items-center gap-2 shrink-0">
            <Tooltip
              content={t("Notificações (em breve)")}
              position="bottom"
              zIndex={10060}
              usePortal
            >
              <button
                className="relative flex items-center justify-center w-9 h-9 rounded-full text-gray-500 hover:text-purple-600 hover:bg-purple-50 transition-colors"
                aria-label={t("Notificações")}
              >
                <Bell className="h-[18px] w-[18px]" />
                {notifCount > 0 && (
                  <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {notifCount}
                  </span>
                )}
              </button>
            </Tooltip>

            <button
              onClick={() => setLanguage(language === "pt" ? "en" : "pt")}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-purple-200 bg-gray-50 hover:bg-purple-100 hover:border-purple-300 transition-all duration-200 text-xs font-semibold text-purple-700 shadow-xs shrink-0"
              title={
                language === "pt" ? "Switch to English" : "Mudar para Português"
              }
            >
              <span>{language === "pt" ? "🇧🇷 PT" : "🇺🇸 EN"}</span>
            </button>

            <TenantSelector variant="chip" />
            <AppLauncher currentApp="smm" portalUrl={window.location.origin} />
            <UserProfileMenu variant="icon-only" />
          </div>
        </header>
        {/* ── Strip GCP-style: subtitle/actions da página atual ──── */}
        {(subtitle || actions) && (
          <div className="hidden lg:flex shrink-0 items-center justify-between min-h-12 px-4 xl:px-5 bg-gray-50 border-b border-purple-200 py-1.5 gap-4 animate-fade-in">
            <div className="flex items-start gap-2.5 min-w-0">
              <Info className="h-4 w-4 text-purple-400 shrink-0 mt-0.5" />
              <div className="text-sm text-gray-600 leading-relaxed max-w-3xl">
                {subtitle}
              </div>
            </div>
            {actions && (
              <div className="flex items-center gap-2 shrink-0">{actions}</div>
            )}
          </div>
        )}

        {/* Page content */}
        <main
          id="main-content"
          className="flex-1 overflow-y-auto w-full min-w-0 bg-white main-scrollbar relative"
        >
          {/* Banner de Reconexão */}
          {isRetrying && (
            <div className="sticky top-0 z-50 w-full animate-in slide-in-from-top-4 duration-300">
              <div className="bg-amber-500/95 backdrop-blur-md text-white px-4 py-2 flex items-center justify-between shadow-lg ring-1 ring-black/5">
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 bg-white/20 rounded-lg flex items-center justify-center">
                    <RefreshCw size={14} className="animate-spin" />
                  </div>
                  <div className="flex flex-col">
                    <p className="text-[11px] font-bold opacity-90">
                      Falha na conexão com o servidor.{" "}
                      {contextError?.message ||
                        "O sistema está tentando reconectar automaticamente..."}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="hidden sm:inline-flex items-center gap-1.5 px-2 py-0.5 bg-white/20 rounded-full text-[9px] font-black uppercase tracking-widest">
                    <div className="w-1 h-1 bg-white rounded-full animate-pulse" />
                    Modo Reconexão Ativa
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="animate-fade-in w-full min-h-full flex flex-col">
            <div className="flex-1">{children}</div>
          </div>
        </main>

        {/* Copyright Footer - Fixed at bottom */}
        <footer className="w-full h-8 sm:h-9 px-4 xl:px-6 border-t border-purple-200 bg-white shrink-0 flex items-center z-20">
          <div className="flex items-center justify-between gap-4 w-full max-w-[1800px] mx-auto">
            <p className="text-[10px] sm:text-[11px] text-gray-400 whitespace-nowrap">
              © {new Date().getFullYear()} Virtus Automation & IA{" "}
              <span className="hidden md:inline">
                . {t("Todos os direitos reservados.")}
              </span>
            </p>
            <div className="flex items-center gap-3 sm:gap-4 text-[10px] sm:text-[11px] text-gray-400 whitespace-nowrap">
              <a
                href="https://virtusautomation.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-purple-600 transition-colors"
              >
                {t("Termos")}
              </a>
              <a
                href="https://virtusautomation.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-purple-600 transition-colors"
              >
                {t("Privacidade")}
              </a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

// ─── Outer: fornece o context e delega para o inner ───────────────────────────
const DashboardLayout: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <PageSubHeaderProvider>
    <DashboardLayoutInner>{children}</DashboardLayoutInner>
  </PageSubHeaderProvider>
);

export default DashboardLayout;
